import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const WhatsAppCard = ({ isHero = false, hideQR = false }) => {
    const ROTATOR_LINK = `${process.env.REACT_APP_API_URL}/join-whatsapp`;

    // Mouse tilt effect
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseXSpring = useSpring(x);
    const mouseYSpring = useSpring(y);

    const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["7deg", "-7deg"]);
    const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-7deg", "7deg"]);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const xPct = mouseX / width - 0.5;
        const yPct = mouseY / height - 0.5;

        x.set(xPct);
        y.set(yPct);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    const products = [];

    return (
        <motion.a
            href={ROTATOR_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
                rotateX,
                rotateY,
                transformStyle: "preserve-3d",
                display: 'block',
                textDecoration: 'none',
                height: isHero ? '100%' : 'auto'
            }}
            initial={isHero ? {} : { opacity: 0, y: 30 }}
            whileInView={isHero ? {} : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
        >
            <div className="whatsapp-banner-horizontal" style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #09090b 0%, #1c0512 50%, #09090b 100%)',
                borderRadius: isHero ? '0' : 'var(--radius-lg)',
                padding: isHero ? '20px 60px' : '30px 40px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: isHero ? '350px' : '220px',
                height: isHero ? '100%' : 'auto',
                overflow: 'hidden',
                border: '1px solid rgba(219, 39, 119, 0.2)',
                boxShadow: '0 10px 30px rgba(219, 39, 119, 0.08), inset 0 0 20px rgba(219, 39, 119, 0.05)',
                cursor: 'pointer'
            }}>
                {/* Background Glow */}
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '120%',
                    height: '120%',
                    background: 'radial-gradient(circle, rgba(219, 39, 119, 0.05) 0%, transparent 70%)',
                    pointerEvents: 'none',
                    zIndex: 0
                }} />

                {/* Background Shapes & Icons (Sensual & Theme-specific watermarks) */}
                <i className="fas fa-heart" style={{
                    position: 'absolute',
                    top: '10px',
                    left: '40%',
                    fontSize: '120px',
                    color: 'var(--primary-color)',
                    opacity: 0.03,
                    pointerEvents: 'none',
                    zIndex: 0,
                    transform: 'rotate(-15deg)'
                }}></i>
                <i className="fas fa-fire" style={{
                    position: 'absolute',
                    bottom: '-30px',
                    left: '52%',
                    fontSize: '140px',
                    color: 'var(--accent-color)',
                    opacity: 0.02,
                    pointerEvents: 'none',
                    zIndex: 0,
                    transform: 'rotate(20deg)'
                }}></i>

                {/* Left: Content Section */}
                <div className="whatsapp-content-container" style={{ 
                    position: 'relative', 
                    zIndex: 2, 
                    textAlign: 'left', 
                    maxWidth: '380px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '12px'
                }}>
                    <div>
                        <h3 style={{ 
                            color: 'var(--text-primary)', 
                            fontSize: isHero ? '28px' : '22px', 
                            fontWeight: '900', 
                            lineHeight: '1.1',
                            marginBottom: '8px',
                            textShadow: '0 4px 20px rgba(0,0,0,0.8)',
                            letterSpacing: '-0.02em',
                            fontFamily: 'var(--font-title)'
                        }}>
                            CLUBE PRIVADO MERCADO ÍNTIMO
                        </h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: isHero ? '14px' : '13px', fontWeight: '500', lineHeight: '1.3' }}>
                            Acesso VIP e discreto aos melhores achadinhos, cupons e lançamentos +18 direto no seu celular.
                        </p>
                    </div>

                    <motion.div
                        whileHover={{ scale: 1.05, backgroundColor: 'var(--accent-color)', color: '#000000' }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                            backgroundColor: 'var(--primary-color)',
                            color: '#ffffff',
                            padding: isHero ? '12px 30px' : '10px 24px',
                            borderRadius: 'var(--radius-sm)',
                            fontWeight: '800',
                            fontSize: isHero ? '15px' : '14px',
                            boxShadow: '0 8px 20px rgba(219, 39, 119, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'all 0.3s ease',
                            border: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                    >
                        <i className="fab fa-whatsapp" style={{ fontSize: '18px' }}></i>
                        Entrar no Grupo
                    </motion.div>
                </div>

                {/* Center: Desired Products */}
                <div className="whatsapp-products-container" style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '30%',
                    right: '35%',
                    zIndex: 1,
                    pointerEvents: 'none'
                }}>
                    {products.map((product, idx) => (
                        <motion.img
                            key={idx}
                            src={product.src}
                            alt="Desejo"
                            style={{
                                position: 'absolute',
                                ...product.style,
                                filter: 'contrast(1.5) brightness(1.2) drop-shadow(0 0 15px rgba(219, 39, 119, 0.4))',
                                pointerEvents: 'none',
                                mixBlendMode: 'screen',
                                WebkitMaskImage: 'radial-gradient(circle, black 40%, transparent 85%)',
                                maskImage: 'radial-gradient(circle, black 40%, transparent 85%)'
                            }}
                            animate={{
                                y: [0, -15, 0],
                                rotate: [product.style.rotate, (parseInt(product.style.rotate) + 5) + 'deg', product.style.rotate]
                            }}
                            transition={{
                                duration: product.duration || 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: product.delay
                            }}
                        />
                    ))}
                </div>

                {/* Right: QR Code Section (Sem scanner laser) */}
                <div className="whatsapp-qrcode-container" style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <motion.div 
                        whileHover={{ scale: 1.08 }}
                        style={{
                            backgroundColor: 'rgba(9, 9, 11, 0.6)',
                            padding: '12px',
                            borderRadius: 'var(--radius-md)',
                            backdropFilter: 'blur(10px)',
                            border: '1.5px solid var(--accent-color)',
                            boxShadow: '0 8px 32px rgba(219, 39, 119, 0.15)'
                        }}
                    >
                        <QRCodeCanvas value={ROTATOR_LINK} size={100} level="H" bgColor="transparent" fgColor="#ffffff" />
                    </motion.div>
                </div>

                {/* Inline Responsive Styles */}
                <style dangerouslySetInnerHTML={{ __html: `
                    @media (max-width: 768px) {
                        .whatsapp-banner-horizontal {
                            flex-direction: column !important;
                            text-align: center !important;
                            padding: 30px !important;
                            gap: 30px !important;
                        }
                        .whatsapp-content-container {
                            text-align: center !important;
                            align-items: center !important;
                            max-width: 100% !important;
                        }
                        .whatsapp-products-container {
                            display: none !important;
                        }
                        .whatsapp-qrcode-container {
                            margin: 0 auto !important;
                        }
                    }
                `}} />
            </div>
        </motion.a>
    );
};

export default WhatsAppCard;
